import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { getThemeClassNames } from 'themes';

import { AppDomain } from 'types/enums/AppDomain';

import { getNewNotifications } from 'store/notifications';

import { ReactComponent as LikesMeIcon } from 'assets/icons/activities/likes-me.svg';
import { ReactComponent as MatchesIcon } from 'assets/icons/activities/matches.svg';
import { ReactComponent as MyLikesIcon } from 'assets/icons/activities/my-likes.svg';
import { ReactComponent as NotificationIcon } from 'assets/icons/activities/notifications.svg';
import { ReactComponent as VisitorsIcon } from 'assets/icons/activities/visitors.svg';

import styles from './activityMenu.module.sass';

enum ActivityTypes {
  Notification = 'notifications',
  Visitors = 'visitors',
  LikesMe = 'likes-me',
  Matches = 'matches',
  MyLikes = 'my-likes',
}

export const ActivityMenu: React.FC = () => {
  const { newLikes, newMatches, newVisits } = useSelector(getNewNotifications);
  const { t } = useTranslation('app');
  const activitiesMenu = [
    {
      id: ActivityTypes.Notification,
      title: t('notifications.title'),
      url: 'notifications',
      icon: <NotificationIcon />,
    },
    {
      id: ActivityTypes.Visitors,
      title: t('notifications.visitors'),
      url: 'visits',
      icon: <VisitorsIcon />,
    },
    {
      id: ActivityTypes.LikesMe,
      title: t('notifications.likesMe'),
      url: 'likes',
      icon: <LikesMeIcon />,
    },
    {
      id: ActivityTypes.Matches,
      title: t('notifications.matches'),
      url: 'matches',
      icon: <MatchesIcon />,
    },
    {
      id: ActivityTypes.MyLikes,
      title: t('myProfile.myLikes'),
      url: 'mylikes',
      icon: <MyLikesIcon />,
    },
  ];

  const btnClassName = getThemeClassNames(styles.navLink, {
    [AppDomain.LocalsDate]: styles.localsDateColor,
    [AppDomain.MePlusYou]: styles.localsDateColor,
    [AppDomain.LustStation]: styles.localsDateColor,
    [AppDomain.Localsjoy]: styles.localsDateColor,
  });

  const counterClassName = getThemeClassNames(styles.activitiesCounter, {
    [AppDomain.LocalsDate]: styles.localsDateColorCounter,
    [AppDomain.MePlusYou]: styles.localsDateColorCounter,
    [AppDomain.LustStation]: styles.localsDateColorCounter,
    [AppDomain.Localsjoy]: styles.localsDateColorCounter,
  });

  const getActivities = (id: ActivityTypes) => {
    let counter = 0;

    switch (id) {
      case ActivityTypes.LikesMe:
        counter = newLikes;
        break;
      case ActivityTypes.Matches:
        counter = newMatches;
        break;
      case ActivityTypes.Visitors:
        counter = newVisits;
        break;
      default:
        break;
    }

    return counter ? <span className={counterClassName}>{counter}</span> : null;
  };

  return (
    <div className={styles.root}>
      <div className={styles.navList}>
        {activitiesMenu.map(({ id, title, url, icon }) => (
          <NavLink
            key={id}
            to={`/activities/${url}`}
            className={btnClassName}
            activeClassName={styles.active}
          >
            <div className={styles.iconWrapper}>
              {icon}
              {getActivities(id)}
            </div>
            {title}
          </NavLink>
        ))}
      </div>
    </div>
  );
};
